<template>
  <v-container fluid>
    <v-row cols="12" v-if="pageLoaded">
      <!-- RENTAL PRODUCT IMAGE -->
      <v-col cols="6" class="left-container">
        <v-card
          :ripple="false"
          :id="rentalProductId"
          class="picture-card"
        >
          <div class="picture">
            <v-col class="pa-0" v-if="rentalProduct.product_picture">
              <v-img class="picture-content"
                     :src="rentalProduct.product_picture"
                     alt="Product Preview"/>
            </v-col>
          </div>
        </v-card>

        <v-row cols="12" class="mt-3" >
          <v-col cols="2">
            <v-btn class="boom-button-back"
                   variant="outlined"
                   @click="backToRentalProductList()"
            >
              {{ $t('COMMON.BUTTON.BACK') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>

      <!-- RENTAL PRODUCT DETAIL -->
      <v-col cols="6">
        <!-- PRODUCT NAME -->
        <v-row cols="12" class="mb-3" justify="space-between" align="center">
          <v-col cols="12">
            <p class="product-name font-weight-black text-uppercase text-h4">
              {{ rentalProduct.product_manufacturer.name }} - {{ rentalProduct.product_boomerang_name }}
            </p>
          </v-col>
        </v-row>

        <!-- RENTAL DESCRIPTION -->
        <p class="mb-3">
          {{ rentalProduct.rental_description }}
        </p>

        <div class="mb-4">
          <!-- RENTAL PRODUCT COST -->
          <p class="price-text ">
            {{ rentalProduct.rental_price.toFixed(2) }} {{ $t('COMMON.EURO') }}
          </p>
          <!-- RENTAL PRODUCT DEPOSIT COST AND SERIAL NUMBER-->
          <span class="text-caption font-weight-thin text-deposit">
            {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.DEPOSIT') }} {{ rentalProduct.rental_deposit.toFixed(2) }}
            {{ $t('COMMON.EURO') }} ({{rentalProduct.rental_serial_number}})
          </span>
        </div>

        <!-- RENTAL PRODUCT CONSUMABLE -->
        <div v-if="rentalProduct.rental_consumable" class="mt-2">
          <p class="font-weight-black mb-2">
            {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.CONSUMABLE_TEXT') }}
          </p>
          <v-card class="pa-2 consumable-card-element no-shadow">
            <v-card-text>
              <v-row cols="12" align="center" justify="space-around">
                <v-col cols="2">
                  <div class="picture-consumable">
                    <v-col class="pa-0" v-if="rentalProduct.rental_consumable.product_picture">
                      <v-img
                        class="picture-content-consumable"
                        :src="rentalProduct.rental_consumable.product_picture"
                        alt="Consumable Preview"/>
                    </v-col>
                  </div>
                </v-col>
                <v-col cols="10">
                  <p class="font-weight-bold">
                    {{ rentalProduct.rental_consumable.product_description }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <!-- RENTAL COST SUMMARY -->
        <v-card class="mt-6 pa-5 no-shadow">
          <v-card-text>
            <p class="font-weight-black d-flex">
              {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.SUMMARY_TEXT') }}
            </p>
          </v-card-text>
          <v-card-text>
            <v-row cols="12" justify="space-between">
              <v-col cols="auto my-0 py-0">
                {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.HT_PRICE') }}
              </v-col>
              <v-col cols="auto my-0 py-0 font-weight-black">
                {{ rentalProduct.rental_price.toFixed(2) }} {{ $t('COMMON.EURO') }}
              </v-col>
            </v-row>
            <v-row cols="12" justify="space-between">
              <v-col cols="auto my-O py-0">
                {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.DEPOSIT') }}
              </v-col>
              <v-col cols="auto my-O py-0 font-weight-black">
                     {{ rentalProduct.rental_deposit.toFixed(2) }} {{ $t('COMMON.EURO') }}
              </v-col>
            </v-row>
            <v-row cols="12" justify="space-between">
              <v-col cols="auto my-O py-0">
                {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.DELIVERY_TOTAL') }}
              </v-col>
              <v-col cols="auto my-O pt-0 pb-3 free-shipping font-weight-black">
                {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.FREE_DELIVERY') }}
              </v-col>
            </v-row>
            <v-divider/>
            <v-row cols="12" justify="space-between" class="py-1">
              <v-col cols="auto font-weight-black">
                {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.HT_TOTAL') }}
              </v-col>
              <v-col cols="auto font-weight-black">
                {{ totalHT() }} {{ $t('COMMON.EURO') }}
              </v-col>
            </v-row>
            <v-divider/>
          </v-card-text>
          <v-card-actions>
            <v-btn
              class="boom-button"
              block
              variant="outlined"
              @click="createRentButton(rentalProduct.technical_id)"
            >
              {{ $t('RENTAL_MODULE_PRODUCT_DETAIL_PAGE.CREATE_RENT_BUTTON') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RentalProductService from '@/services/rentalModule/RentalProductService'
import { displayToast, handleResponseError } from '@/components/utils/globalFunction'
import AvailabilityStatusConstant from '@/constants/rentalModule/availabilityStatusConstant'

  export default {
    name: 'RentalProductDetailPage',
    data() {
      return {
        loading: false,
        pageLoaded: false,
        rentalProductId: null,
        rentalProduct: null
      }
    },
    async created() {
      this.rentalProductId = this.$route.params.rentalProductId
      if (!this.rentalProductId) {
        this.backToRentalProductList()
      } else {
        await this.getRentalProductDetail()
        this.pageLoaded = true
      }
    },
    methods: {
      async getRentalProductDetail() {
        this.loading = true
        try {
          this.rentalProduct =
            await RentalProductService.getRentalProductDetailById(this.rentalProductId)
          if (this.rentalProduct.rental_status !== AvailabilityStatusConstant.AVAILABLE) {
            displayToast(this.$t('RENTAL_MODULE_LIST_PRODUCTS_PAGE.AVAILABILITY_ERROR'), 'error')
            this.backToRentalProductList()
          }
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      },
      createRentButton(rentalProductId) {
        this.$router.push('/rental-module/rental-file-create/' + rentalProductId)
      },
      backToRentalProductList() {
        this.$router.push('/rental-module/product-list/'
          + this.$store.getters['auth/getRentalRepairFileBoomerangId'])
      },
      totalHT() {
        return (this.rentalProduct.rental_price
          + this.rentalProduct.rental_deposit).toFixed(2)
      }
    }
  }
</script>

<style scoped>
.v-container {
    padding: 32px;
}

.picture-card {
  box-shadow: unset;
  max-width: 90%;
  border: 1px solid #DADADA;
  padding: 10px;
  border-radius: 30px;

  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: center;
  align-items: center;

  min-height: 400px;
  height: 400px;
  max-height: 400px;

  .picture .picture-content{
    margin: auto;
    width: 300px;
    height: 300px;
  }
}

.consumable-card-element {
  border: 1px solid #559a1b;
  padding: 10px;
  border-radius: 10px;

  .picture-consumable .picture-content-consumable {
    margin: auto;
    width: 90px;
    height: 90px;
  }
}

.product-name {
  align-self: self-start;
}

.boom-button {
  height: 80px;
}

.price-text {
  font-size: 40px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  color: #549C14;
}

.free-shipping {
  color: #8CBB63;
}

.text-deposit {
  color: #B3B3B3;
}

.no-shadow {
  box-shadow: unset;
}

.left-container {
  justify-content: space-between;
  flex-direction: column;
  display: flex;
}
</style>
